import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommandIcon, XIcon } from 'lucide-react';
import { cn, useDebouncedCallback } from '@noor/lib';
import { useHeaderSearchStore } from '../model/header-search-store';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Global search field of the app header (Figma 1212:8223): 263×38 outlined
 * box with a ⌘ + F hint. It drives whichever page registered through
 * `useHeaderSearch`; with no page bound it stays visible but disabled.
 *
 * Owns only its local draft. An external value change (clear-all, back/forward,
 * navigating to another page) re-seeds it via effect — never via `key` remount,
 * so the debounced request can't steal focus.
 */
export function HeaderSearch({ className }: { className?: string }) {
  const { t } = useTranslation('common');
  const binding = useHeaderSearchStore((s) => s.binding);
  const value = binding?.value ?? '';

  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(value);
  // Last value written to the page — tells our own commit apart from an
  // external change.
  const lastCommittedRef = useRef(value);

  const commit = useDebouncedCallback((next: string) => {
    const trimmed = next.trim();
    lastCommittedRef.current = trimmed;
    binding?.onChange(trimmed);
  }, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (value === lastCommittedRef.current) return;
    lastCommittedRef.current = value;
    setDraft(value);
  }, [value]);

  // ⌘/Ctrl + F focuses the field instead of the browser's find bar.
  useEffect(() => {
    if (!binding) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [binding]);

  const placeholder = binding?.placeholder ?? t('search.placeholder');

  return (
    <div
      data-slot="header-search"
      className={cn(
        'relative h-[38px] w-[263px] shrink-0 rounded-lg border border-input-border bg-transparent transition-[border-color,box-shadow] focus-within:border-input-border-focus focus-within:ring-[3px] focus-within:ring-ring/50',
        'has-[input:disabled]:cursor-not-allowed has-[input:disabled]:bg-form-control-bg-disabled',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="search"
        autoComplete="off"
        disabled={!binding}
        value={draft}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(event) => {
          setDraft(event.target.value);
          commit(event.target.value);
        }}
        className="h-full w-full min-w-0 appearance-none rounded-lg border-0 bg-transparent py-2 pr-[68px] pl-3 text-sm tracking-[-0.28px] text-input-fg outline-none placeholder:text-input-placeholder disabled:pointer-events-none disabled:cursor-not-allowed disabled:placeholder:text-form-control-fg-disabled [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
      />
      {draft !== '' && binding ? (
        <button
          type="button"
          aria-label={t('search.clear')}
          onClick={() => {
            setDraft('');
            lastCommittedRef.current = '';
            commit.cancel();
            binding.onChange('');
            inputRef.current?.focus();
          }}
          className="absolute top-1/2 right-3 flex size-5 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <XIcon className="size-4" aria-hidden="true" />
        </button>
      ) : (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 right-3 flex -translate-y-1/2 items-center gap-0.5 text-muted-foreground"
        >
          <CommandIcon className="size-[18px]" />
          <span className="text-sm leading-none">+ F</span>
        </span>
      )}
    </div>
  );
}
